import {
  BRAND_LIST,
  BRAND_NAME_FOR_META,
  BRAND_NAME_LIST,
  GABrandList,
  OptionsType,
  Point,
  ImageRatio,
  SizeGender
} from "@type/index";
import { Cookies } from "react-cookie";
import { COUNTRY, LANGUAGE, COUNTRY_CONFIG } from "@type/country";
import { getUA } from "react-device-detect";
import { head } from "lodash";
import {
  FR_UserAgent,
  guAndroidPattern,
  guIOSPattern,
  REACT_APP_BRAND_NAME,
  UQ_JP_JA_Android_UserAgent,
  UQ_JP_JA_Ios_UserAgent,
  UQ_US_EN_Android_UserAgent,
  UQ_US_EN_Ios_UserAgent
} from "@util/config";
import { CurrencyFormatter } from "@util/currency";
import { convertRegionNameTempForUgc } from "@util/country";

/**
 * Convert file string to base64
 * @param file
 */
export const fileToBase64 = (file: File | null): Promise<string> =>
  new Promise((resolve, reject) => {
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e: any) => {
        resolve(e.target.result || e.currentTarget.result);
      };
      reader.onerror = (err) => {
        reject(err);
      };
    } else {
      reject();
    }
  });
/**
 * Convert base64 string to file
 * @param base64Data
 * @param filename
 */
export const base64ToFile = (
  base64Data: string | null,
  filename = "file"
): Promise<File> =>
  new Promise((resolve, reject) => {
    if (base64Data) {
      const arr = base64Data.split(",");
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      // eslint-disable-next-line no-plusplus
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      resolve(new File([u8arr], filename, { type: mime }));
    } else {
      reject();
    }
  });
/**
 * change Html Attribute lang
 * @param lang language
 */
export const changeHtmlLanguage = (lang: string) => {
  window.document.querySelector("html")?.setAttribute("lang", lang);
};
/**
 * set html keywords of meta
 * @param keywords
 */
export const setKeywords = (keywords: string) => {
  const titleEle = document.querySelector("meta[name=keywords]");
  if (titleEle) {
    titleEle.setAttribute("content", keywords);
  }
};
/**
 * set html title of meta
 * @param title
 */
export const setTitle = (title: string) => {
  const titleEle = document.querySelector("title");
  if (titleEle) {
    titleEle.innerHTML = title;
  }
};
/**
 * set html description of meta
 * @param description
 */
export const setDescription = (description: string) => {
  const titleEle = document.querySelector("meta[name=description]");
  if (titleEle) {
    titleEle.setAttribute("content", description);
  }
};
/**
 * Native app or not
 */
export const isNativeApp = () => {
  if (window) {
    // ios ex:  Mozilla/5.0 (iPhone; CPU iPhone OS 15_2 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) FRNativeApp/uq-ios-jp-7.15.5/com.uniqlo.catalogue
    // android ex: Mozilla/5.0 (Linux; Android 12; Pixel 5 Build/SP1A.211105.003; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/91.0.4472.114 Mobile Safari/537.36 FRNativeApp/uq-android-jp-7.15.0/com.uniqlo.ja.catalogue
    return window.navigator.userAgent.indexOf("FRNativeApp") > -1;
  }
  return false;
};

export const getSafariVersion = (): number | null => {
  const userAgent = window.navigator.userAgent;
  let version = null;
  if (userAgent.includes("Safari") && userAgent.includes("OS")) {
    const agents = userAgent.split(/OS |like/);
    if (agents.length > 2) {
      version = head(agents[1].split("_")) || "";
    }
  }
  return version ? parseInt(version, 10) : null;
};

export const toThousands = (
  num: number | null,
  noNeedToFixed?: boolean
): string => {
  if (num === null) {
    return "";
  }
  const region = getRegionFromUrl();
  const language = getLanguageFromUrl();
  const result = [];
  let counter = 0;
  let _num = num.toString();
  if (!noNeedToFixed) {
    switch (`${language}-${region}`) {
      case "ja-jp": {
        const isFloat = _num.indexOf(".") > 0;
        if (isFloat) {
          _num = Number.parseFloat(_num).toFixed(2);
        }
        break;
      }
      default:
        _num = Number.parseFloat(_num).toFixed(2);
        break;
    }
  }
  const numArr = _num.split("."); // dividing decimal point
  const numStrs = numArr[0].toString().split(""); // convert to a character array
  for (let i = numStrs.length - 1; i >= 0; i -= 1) {
    counter += 1;
    result.unshift(numStrs[i]);
    if (!(counter % 3) && i !== 0) {
      result.unshift(",");
    }
  }
  if (numArr[1]) {
    return result.join("").concat(`.${numArr[1]}`);
  } else {
    return result.join("");
  }
};

export const uniqueArrayByKey = (arr: any[], key: string) => {
  if (!key) return arr;
  const ress = new Map();
  return arr.filter((a) => !ress.has(a[key]) && ress.set(a[key], 1));
};
/**
 * filter cover point
 * @param points already point list
 * @param xPoint current point 'x' of position
 * @param yPoint current point 'y' of position
 * @param safeWidth current safewidth percent about search img
 * @param direction computed direction (default: right-bottom)
 */
export const computedSafePoint = (
  points: Point[],
  xPoint: number,
  yPoint: number,
  safeWidth: number,
  direction = "right-bottom"
): [number, number] => {
  let isSafe = true;
  let isX = true;
  let _direction = direction;
  points.forEach((point: Point) => {
    const isOutX = Math.abs(xPoint - point.x) < safeWidth;
    const isOutY = Math.abs(yPoint - point.y) < safeWidth;
    // 是否会有交集
    if (isOutX && isOutY) {
      isSafe = false;
      const isNeedChangeX =
        Math.abs(xPoint - point.x) < Math.abs(yPoint - point.y);
      if (isNeedChangeX) {
        isX = true;
        if (xPoint - point.x < 0) {
          _direction = "left-top";
        } else {
          _direction = "right-bottom";
        }
      } else {
        isX = false;
        if (yPoint - point.y < 0) {
          _direction = "left-top";
        } else {
          _direction = "right-bottom";
        }
      }
    }
  });
  if (isSafe) {
    return [xPoint, yPoint];
  } else {
    const nextXPoint =
      _direction === "right-bottom" ? xPoint + safeWidth : xPoint - safeWidth;
    const nextYPoint =
      _direction === "right-bottom" ? yPoint + safeWidth : yPoint - safeWidth;
    // Tail Call
    const safePoint = isX
      ? computedSafePoint(points, nextXPoint, yPoint, safeWidth, _direction)
      : computedSafePoint(points, xPoint, nextYPoint, safeWidth, _direction);
    return safePoint;
  }
};
/**
 * only 4 developer used
 * @param res visualSearch response
 */
export const _devDebuggerDotScope = (res: any) => {
  const imgDom = document.querySelector("img[alt='example image']");
  if (imgDom) {
    const cvsDom = document.createElement("canvas");
    cvsDom.width = imgDom.clientWidth;
    cvsDom.height = imgDom.clientHeight;
    if (imgDom.parentElement) {
      cvsDom.style.position = "absolute";
      cvsDom.style.top = "0";
      cvsDom.style.left = "0";
      imgDom.parentElement.append(cvsDom);
      const boundInfos = res.result.items.map(
        (item: any) => item.localized_info
      );
      boundInfos.forEach((boundInfo: any) => {
        const ctx = cvsDom.getContext("2d");
        if (ctx) {
          const boundPoint = boundInfo.bounding_poly.normalized_vertices;
          ctx.lineWidth = 1;
          ctx.strokeStyle = "#0f0";
          ctx.moveTo(
            boundPoint[0].x * cvsDom.width,
            boundPoint[0].y * cvsDom.height
          );
          boundPoint.forEach((bound: any) => {
            ctx.lineTo(bound.x * cvsDom.width, bound.y * cvsDom.height);
          });
          ctx.lineTo(
            boundPoint[0].x * cvsDom.width,
            boundPoint[0].y * cvsDom.height
          );
          ctx.stroke();
        }
      });
    }
  }
};

export const changeFileType = (file: File) =>
  new Promise((resolve) => {
    if (file.type === "image/webp" || file.type === "image/gif") {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = function (event: any) {
        const image: any = new Image();
        image.src = event.target.result;
        image.onload = function () {
          const canvas = document.createElement("canvas");
          const imageCanvas: any = canvas.getContext("2d");
          canvas.width = image.width;
          canvas.height = image.height;
          imageCanvas.drawImage(image, 0, 0, image.width, image.height);
          const zipBase64 = canvas.toDataURL("image/jpeg");
          base64ToFile(zipBase64).then((imageRes) => {
            resolve(imageRes);
          });
        };
      };
    } else {
      resolve(file);
    }
  });

export const formatDecimal = (num: number, decimal: number) => {
  let newNum = num.toString();
  const index = newNum.indexOf(".");
  if (index !== -1) {
    newNum = newNum.substring(0, decimal + index + 1);
    newNum = parseFloat(newNum).toFixed(decimal);
  } else {
    newNum = toThousands(Number(newNum), true);
  }
  return newNum;
};

export const formatTotalNum = (lang: string, num: number) => {
  const lang_other = [
    { range: 1, symbol: "", value: 1 },
    { range: 1e4, symbol: "K", value: 1e3 },
    { range: 1e6, symbol: "M", value: 1e6 },
    { range: 1e9, symbol: "B", value: 1e9 }
  ];
  const lang_ja = [
    { range: 1, symbol: "", value: 1 },
    { range: 1e4, symbol: "万", value: 1e4 },
    { range: 1e8, symbol: "億", value: 1e8 }
  ];
  let si = lang_ja;
  if (lang !== "ja") {
    si = lang_other;
  }
  let i;
  for (i = si.length - 1; i > 0; i -= 1) {
    if (num >= si[i].range) {
      break;
    }
  }
  return formatDecimal(num / si[i].value, 1) + si[i].symbol;
};

export const isClient = typeof window !== "undefined";
let firstPageView = true;

export function checkPreviousPageExists() {
  if (!isClient) {
    return false;
  }

  if (!firstPageView) {
    return true;
  }
  firstPageView = false;
  if (
    window.performance &&
    window.performance
      .getEntriesByType("navigation")
      .map((nav: any) => nav.type)
      .includes("reload")
  ) {
    return true;
  }

  return !!(
    document.referrer &&
    document.referrer.includes(new URL(window.location.href).hostname)
  );
}

export const isApp = () =>
  getUA.indexOf(UQ_JP_JA_Ios_UserAgent) > -1 ||
  getUA.indexOf(UQ_JP_JA_Android_UserAgent) > -1 ||
  getUA.indexOf(UQ_US_EN_Ios_UserAgent) > -1 ||
  getUA.indexOf(UQ_US_EN_Android_UserAgent) > -1 ||
  getUA.indexOf(FR_UserAgent) > -1 ||
  guIOSPattern.test(getUA) ||
  guAndroidPattern.test(getUA);

export const formatInchParams = (heightInch: string) => {
  const isInchCountry =
    window.location.href.indexOf(`${window.location.origin}/us/en`) > -1 ||
    window.location.href.indexOf(`${window.location.origin}/uk/en`) > -1;
  if (isInchCountry) {
    if (heightInch === "0") {
      return 0;
    } else {
      let foot = 0;
      let inch = 0;
      foot = Number(heightInch.slice(0, heightInch.indexOf("′")));
      inch = Number(
        heightInch.slice(heightInch.indexOf("′") + 1, heightInch.indexOf("″"))
      );
      return foot * 12 + inch;
    }
  } else {
    return null;
  }
};

export const getRegionFromUrl = () => {
  const temp = window.location.pathname;
  const regex = /\/(.+?)\//g;
  const result = regex.exec(temp) || "jp";
  return result[1];
};

export const getLanguageFromUrl = () => {
  const temp = window.location.pathname.slice(1);
  const regex = /\/(.+?)\//g;
  const result = regex.exec(temp) || "";
  return result[1];
};

export const isNotEmptyStr = (s: unknown): boolean => {
  if (typeof s === "string" && s.length > 0) {
    return true;
  }
  return false;
};

export const footAndInches: COUNTRY_CONFIG<boolean> = {
  jp: false,
  us: true,
  uk: true,
  fr: false,
  de: false,
  es: false,
  it: false,
  dk: false,
  se: false,
  nl: false,
  be: false,
  fi: false,
  gr: false,
  ie: false,
  lu: false,
  at: false,
  pl: false,
  pt: false,
  si: false,
  sk: false,
  lt: false,
  lv: false,
  ee: false,
  cz: false,
  hu: false,
  ro: false,
  bg: false,
  hr: false,
  ca: false
};

// inch conversion
export const heightInchFromTotalInch = (totalInch: number) =>
  Math.floor(totalInch % 12);

export const heightFootFromTotalInch = (totalInch: number) =>
  Math.floor(totalInch / 12);

export const heightFootInchFromTotalInch = (totalInch: number) => {
  const foot = heightFootFromTotalInch(totalInch);
  const inch = heightInchFromTotalInch(totalInch);
  return `${foot}′ ${inch}″`;
};

export const userHeight = (height: number): string => {
  const country = getRegionFromUrl() as COUNTRY;
  if (footAndInches[country])
    return height === 0 ? "-′-″" : heightFootInchFromTotalInch(height);
  else return height === 0 ? "-" : height?.toString() ?? "-";
};

// handle height label
export const handleHeightLabel = (item: OptionsType, t: any): string => {
  const language = getLanguageFromUrl();
  let str = "";
  const min = isNotEmptyStr(item.min);
  const max = isNotEmptyStr(item.max);
  const displayMin = userHeight(Number(item.min));
  const displayMax = userHeight(Number(item.max));
  switch (language) {
    case "ja": {
      if (min && !max) {
        str = `${displayMin}${t("text_style_model_height")}${t(`${item.lid}`)}`;
      } else if (!min && max) {
        str = `${displayMax}${t("text_style_model_height")}${t(`${item.lid}`)}`;
      } else if (min && max) {
        str = `${displayMin}${t("text_style_model_height")} - ${displayMax}${t(
          "text_style_model_height"
        )}`;
      } else {
        str = t(`${item.lid}`);
      }
      break;
    }
    default: {
      if (min && !max) {
        str = `${t(`${item.lid}`)} ${displayMin}${t(
          "text_style_model_height"
        )}`;
      } else if (!min && max) {
        str = `${t(`${item.lid}`)} ${displayMax}${t(
          "text_style_model_height"
        )}`;
      } else if (min && max) {
        str = `${displayMin}${t("text_style_model_height")} - ${displayMax}${t(
          "text_style_model_height"
        )}`;
      } else {
        str = t(`${item.lid}`);
      }
      break;
    }
  }
  return str;
};

export const getUrlParamaObj = () => {
  let url = window.location.search;
  url = url.substring(1);
  const dataObj: any = {};
  if (url.indexOf("&") > -1) {
    const _url = url.split("&");
    for (let i = 0; i < _url.length; i += 1) {
      const arr = _url[i].split("=");
      dataObj[arr[0]] = arr[1];
    }
  } else {
    const _url = url.split("=");
    dataObj[_url[0]] = _url[1];
  }
  return dataObj;
};

export const getUrlParamaObjCustom = (url: string) => {
  url = url.substring(1);
  const dataObj: any = {};
  if (url.indexOf("&") > -1) {
    const _url = url.split("&");
    for (let i = 0; i < _url.length; i += 1) {
      const arr = _url[i].split("=");
      dataObj[arr[0]] = arr[1];
    }
  } else {
    const _url = url.split("=");
    dataObj[_url[0]] = _url[1];
  }
  return dataObj;
};

/*
  GU brand must be capitalized for Google Analytics,
  while the internal BRAND_LIST is lowercase "gu"
*/
export const transformBrandForGoogleAnalytics = (
  brand: string
): GABrandList => {
  switch (brand) {
    case BRAND_LIST.UQ:
      return GABrandList.UQ;
    case BRAND_LIST.GU:
      return GABrandList.GU;
    default:
      return GABrandList.UQ;
  }
};

// Capitalize the first letter
export const titleCase = (str: string): string => {
  let newStr = "";
  if (str && str.length > 0) {
    newStr = str.slice(0, 1).toUpperCase() + str.slice(1).toLowerCase();
  }
  return newStr;
};

// Get brand name
export const getBrandName = (): string => {
  const brand_name = getBrand();
  switch (brand_name) {
    case BRAND_LIST.UQ:
      return titleCase(BRAND_NAME_LIST.UQ);
    case BRAND_LIST.GU:
      return BRAND_NAME_LIST.GU.toUpperCase();
    default:
      return titleCase(BRAND_NAME_LIST.UQ);
  }
};

// Intercepted brand from domain
export const getBrand = () => {
  if (REACT_APP_BRAND_NAME) {
    return REACT_APP_BRAND_NAME;
  } else {
    const _brand = window.location.hostname;
    if (_brand.includes(BRAND_NAME_LIST.UQ)) {
      return BRAND_LIST.UQ;
    }
    if (_brand.includes(BRAND_NAME_LIST.GU)) {
      return BRAND_LIST.GU;
    }
    return BRAND_LIST.UQ;
  }
};

//Get brand for meta
export const getBrandForMeta = () => {
  const brand_name_for_meta = getBrand();
  switch (brand_name_for_meta) {
    case BRAND_LIST.UQ:
      return BRAND_NAME_FOR_META.UQ;
    case BRAND_LIST.GU:
      return BRAND_NAME_FOR_META.GU;
    default:
      return BRAND_NAME_FOR_META.UQ;
  }
};

export const handleDuplicate = (arr: any, name: string) => {
  const resArr: any = [];
  arr.map((item: any) => {
    let hasDuplicate = false;
    resArr.map((res: any) => {
      if (res[name] === item[name]) {
        hasDuplicate = true;
      }
      return null;
    });
    if (!hasDuplicate) {
      resArr.push(item);
    }
    return null;
  });
  return resArr;
};

export const handleSelectSizeToUrl = (totalArr: any, targetArr: any) => {
  const res: any = [];
  targetArr.map((itemOne: any) => {
    totalArr.map((itemTwo: any) => {
      if (itemOne.name === itemTwo.name) {
        res.push(itemTwo);
      }
      return null;
    });
    return null;
  });
  return res;
};

export const handleOneSizeFormatMore = (totalArr: any, targetArr: any) => {
  const _sizes: any = [];
  const _sizeName: any = [];
  targetArr.map((itemOne: any) => {
    totalArr.map((itemTwo: any) => {
      if (itemOne === itemTwo.code && _sizeName.indexOf(itemTwo.name) === -1) {
        _sizeName.push(itemTwo.name);
      }
      return null;
    });
    return null;
  });
  _sizeName.map((itemOne: any) => {
    totalArr.map((itemTwo: any) => {
      if (itemOne === itemTwo.name) {
        _sizes.push(itemTwo.code);
      }
      return null;
    });
    return null;
  });
  return _sizes;
};

export const handleMoreSizeCodeToLessSize = (totalArr: any, targetArr: any) => {
  const name: any = [];
  targetArr.map((itemOne: any) => {
    totalArr.map((itemTwo: any) => {
      if (itemOne === itemTwo.code) {
        name.push(itemTwo.name);
      }
      return null;
    });
    return null;
  });
  return Array.from(new Set(name));
};

export const handleSelectGenderHeight = (gender: any) => {
  const _gender = gender.toLocaleLowerCase();
  if (_gender.includes("women")) {
    return "women";
  } else if (_gender.includes("men")) {
    return "men";
  } else if (_gender.includes("kids・baby（キッズ・ベビー・子供服）")) {
    return "kidsandbaby";
  } else if (_gender.includes("kids・baby")) {
    return "kidsandbaby";
  } else if (_gender.includes("kids")) {
    return "kids";
  } else if (_gender.includes("baby")) {
    return "baby";
  } else {
    return "";
  }
};

// Prevents events from bubbling
export const preventBubbling = (
  e: React.MouseEvent<HTMLButtonElement, MouseEvent>
) => {
  e.stopPropagation();
  e.preventDefault();
};

/**
 * @param priceValue - a price value
 */
export const formatPrice = (priceValue: number): string => {
  const region = getRegionFromUrl();
  const language = getLanguageFromUrl();
  const price = new CurrencyFormatter(
    convertRegionNameTempForUgc(region) as COUNTRY,
    language as LANGUAGE
  );
  return price.format(priceValue);
};

/**
 * Function checks the size of device and returns if its a mobile
 * or not, for IPad it will be false
 * Function uses the same width as DS breakpoints
 */
export const isSP = () => window && window.innerWidth < 600;
export const isNotEmptyObj = (s: any): boolean => {
  if (s === undefined || s === null) {
    return false;
  }
  const count = Object.keys(s).length;
  if (count > 0) {
    return true;
  }
  return false;
};

export function getIsLoggedIn(): boolean {
  const cookies = new Cookies();
  return cookies.get("isLoggedIn") === "true";
}

export const getEcRenewalCompatibility = (): boolean =>
  process.env.REACT_APP_EC_RENEWAL_COMPATIBILITY === "3";

// Item Image Ratio for ECRenewal
export const getEcRenewalImageRatio = (): ImageRatio => {
  if (process.env.REACT_APP_EC_RENEWAL_RATIO === "3x4") {
    return "3x4";
  } else if (process.env.REACT_APP_EC_RENEWAL_RATIO === "1x1") {
    return "1x1";
  } else {
    return "3x4";
  }
};

// Gender mapping
const genderMapper: { [key: string]: string } = {
  MEN: "text_men",
  WOMEN: "text_women",
  KIDS: "text_kids",
  BABY: "text_baby",
  UNISEX: "text_unisex"
};

export const localizeGenderName = (
  t: any,
  genderCategory: string | undefined
) => {
  if (!genderCategory || !genderMapper[genderCategory]) return "";
  return t(genderMapper[genderCategory]) || "";
};

// SizeGender mapping for the Unisex Flag and GenderDisplay
const sizeGenderMapper: { [key in SizeGender]: string } = {
  MEN: "text_men",
  WOMEN: "text_women",
  KIDS: "text_kids",
  BABY: "text_baby",
  NO_GENDER: ""
};

/* sizeGender will be an enum with values: MEN, WOMEN, KIDS, BABY, NO_GENDER
 * and the localization mapping will be based on the sizeGenderMapper
 */
export const localizeSizeGenderName = (
  t: any,
  sizeGender: SizeGender | undefined,
  // Fix: when the cached response is received, we might not have sizeGender
  // but we still want to show the gender hence the fallback to genderCategory localization
  genderCategory?: string | undefined
): string => {
  if (!sizeGender) return localizeGenderName(t, genderCategory);
  const mappedTranslationKey = sizeGender ? sizeGenderMapper[sizeGender] : "";
  return mappedTranslationKey ? t(mappedTranslationKey) ?? "" : "";
};
